import { trans } from "@mongez/localization";
import { cn } from "core/shared";
import { useEffect, useState } from "react";
import { BaseLink, Button, TextInput } from "~/design-system/components";
import { searchAtom } from "~/modules/shared/atoms";
import { useSearchProducts } from "~/modules/shared/hooks";
import { URLS } from "~/shared/urls";
import { ProductsSearchResult } from "./ProductsSearchResult";
import { RecentSearches } from "./RecentSearches";

export function HeaderSearch() {
  const [searchValue, setSearchValue] = useState("");
  const searchOpened = searchAtom.useOpened();

  const {
    products,
    loading,
    search,
    clearSearchResults,
    recentSearches,
    handleRecentSearch,
  } = useSearchProducts();

  useEffect(() => {
    if (searchOpened) {
      document.body.style.overflow = "hidden";
    } else {
      setSearchValue("");
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
      setSearchValue("");
    };
  }, [searchOpened]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    search(value);
  };

  return (
    <div className="relative">
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            searchAtom.close();
          }
        }}
        className={cn(
          "absolute inset-0 z-50 h-0 w-full overflow-hidden bg-black/70 transition-all duration-300",
          searchOpened && "h-[calc(100dvh-var(--header-height))]",
        )}
      >
        <div className="flex h-full flex-col gap-4 md:gap-6">
          {/* Search Input */}
          <div className="bg-white/90 p-4 backdrop-blur-lg md:p-6">
            <div className="relative mx-auto flex max-w-[1600px] items-center justify-between gap-6">
              <TextInput
                name="search"
                placeholder="Search"
                value={searchValue}
                onChange={handleSearch}
                classes={{
                  input:
                    "w-full bg-transparent rounded-none border-none focus:ring-primary-light rounded-full focus:ring-1",
                }}
              />
              <Button
                onClick={() => setSearchValue("")}
                title={trans("clearSearch")}
                aria-label={trans("clearSearch")}
                className="absolute top-1/2 -translate-y-1/2 font-medium hover:underline ltr:right-2 rtl:left-2"
              >
                {trans("clear")}
              </Button>
            </div>
          </div>
          {/* Search Results */}
          <div className="px-4 md:px-6">
            <div className="mx-auto flex max-h-[500px] w-full max-w-[1600px] flex-col overflow-auto rounded-3xl bg-white p-6">
              {/* Header */}
              <div className="border-border flex items-center justify-between gap-6 border-b pb-4">
                <h2 className="text-xl font-semibold uppercase">
                  {searchValue
                    ? trans("searchResults")
                    : trans("recentSearches")}
                </h2>
                {searchValue ? (
                  <BaseLink
                    to={URLS.ourCakes.list + `?q=${searchValue}`}
                    title={trans("viewAll")}
                    aria-label={trans("viewAll")}
                    onClick={() => {
                      setSearchValue("");
                      searchAtom.close();
                      handleRecentSearch(searchValue);
                    }}
                    className="font-medium hover:underline"
                  >
                    {trans("viewAll")}
                  </BaseLink>
                ) : (
                  <Button
                    onClick={clearSearchResults}
                    title={trans("clearAll")}
                    aria-label={trans("clearAll")}
                    className="font-medium hover:underline"
                  >
                    {trans("clearAll")}
                  </Button>
                )}
              </div>
              {/* Results */}
              {searchValue ? (
                <ProductsSearchResult loading={loading} products={products} />
              ) : (
                <RecentSearches
                  recentSearches={recentSearches}
                  handleSearch={handleSearch}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
